import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { LocalDate } from "@/components/ui/local-date";
import { PAGE_SIZE_OPTIONS, Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { Select } from "@/components/ui/select";
import { useUrlTableSort } from "@/components/ui/sortable-table";
import { ApiError, api } from "@/lib/api";
import { paginationSearchFields, sortSearchFields } from "@/lib/search";
import { formatCurrency } from "@/lib/utils";
import { Link, createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

/**
 * `/billing` — accountant/admin-only manual invoicing, listing page.
 *
 * Generating a report ("výkaz") is its own page, reached via the
 * "Generate report" button. This page is the invoice-batch ledger:
 * a filterable table where every row clicks through to the report
 * detail, plus the running paid / pending totals.
 */

const BATCH_STATUSES = ["draft", "processing", "sent", "paid", "failed"] as const;

const SORT_KEYS = [
  "customer",
  "period",
  "status",
  "amount",
  "fakturoid",
  "items",
  "createdAt",
] as const;
type SortKey = (typeof SORT_KEYS)[number];

const billingSearchSchema = z.object({
  q: z.string().optional(),
  status: z.enum(BATCH_STATUSES).optional(),
  customerId: z.string().optional(),
  ...sortSearchFields(SORT_KEYS),
  ...paginationSearchFields,
});

type BillingSearch = z.infer<typeof billingSearchSchema>;

type InvoiceBatch = {
  id: string;
  customer: { id: string; name: string };
  periodFrom: string;
  periodTo: string;
  status: "draft" | "processing" | "sent" | "paid" | "failed";
  totalAmount: string | null;
  fakturoidInvoiceNumber: string | null;
  dueOn: string | null;
  paidAt: string | null;
  createdAt: string;
  _count: { items: number };
};

type CustomerOption = { id: string; name: string };

type BillingResponse = {
  batches: InvoiceBatch[];
  total: number;
  page: number;
  pageSize: number;
  stats: { totalPaid: number; totalPending: number };
};

const statusVariant: Record<
  string,
  "default" | "secondary" | "success" | "warning" | "destructive" | "outline"
> = {
  draft: "secondary",
  processing: "default",
  sent: "warning",
  paid: "success",
  failed: "destructive",
};

/** A `sent` batch past its due date with no payment recorded. */
function isOverdue(b: InvoiceBatch): boolean {
  return b.status === "sent" && !b.paidAt && b.dueOn !== null && new Date(b.dueOn) < new Date();
}

export const Route = createFileRoute("/_app/billing/")({
  validateSearch: billingSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
    status: search.status,
    customerId: search.customerId,
    sort: search.sort,
    dir: search.dir,
    page: search.page,
    pageSize: search.pageSize,
  }),
  loader: async ({ deps }) => {
    const params = new URLSearchParams();
    if (deps.q) params.set("q", deps.q);
    if (deps.status) params.set("status", deps.status);
    if (deps.customerId) params.set("customerId", deps.customerId);
    if (deps.sort) params.set("sort", deps.sort);
    if (deps.dir) params.set("dir", deps.dir);
    if (deps.page) params.set("page", String(deps.page));
    if (deps.pageSize) params.set("pageSize", String(deps.pageSize));
    const qs = params.toString();
    try {
      const [billing, customers] = await Promise.all([
        api.get<BillingResponse>(qs ? `/api/billing?${qs}` : "/api/billing"),
        api.get<{ customers: CustomerOption[] }>("/api/customers?pageSize=200"),
      ]);
      return { billing, customers: customers.customers };
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        throw redirect({ to: "/my" });
      }
      throw err;
    }
  },
  component: BillingPage,
});

function BillingPage() {
  const { billing, customers } = Route.useLoaderData();
  const { batches, total, page, pageSize, stats } = billing;
  const search: BillingSearch = Route.useSearch();
  const navigate = useNavigate();

  const sort = useUrlTableSort({
    sortKey: search.sort ?? null,
    sortDir: search.dir ?? "asc",
    onChange: (key, dir) =>
      navigate({
        to: "/billing",
        search: { ...search, sort: key as SortKey, dir, page: undefined },
      }),
  });

  function setFilter(key: "status" | "customerId", value: string | undefined) {
    navigate({ to: "/billing", search: { ...search, [key]: value, page: undefined } });
  }

  const columns: Array<DataTableColumn<InvoiceBatch>> = [
    {
      key: "customer",
      header: "Customer",
      sortAccessor: (b) => b.customer.name,
      cellClassName: "font-medium",
      cell: (b) => (
        <Link to="/billing/$id" params={{ id: b.id }} className="text-primary hover:underline">
          {b.customer.name}
        </Link>
      ),
    },
    {
      key: "period",
      header: "Period",
      sortAccessor: (b) => b.periodFrom,
      cellClassName: "text-sm",
      cell: (b) => (
        <>
          <LocalDate value={b.periodFrom} /> — <LocalDate value={b.periodTo} />
        </>
      ),
    },
    {
      key: "status",
      header: "Status",
      sortAccessor: (b) => b.status,
      cell: (b) => (
        <div className="flex items-center gap-1.5">
          <Badge variant={statusVariant[b.status] ?? "outline"}>{b.status}</Badge>
          {isOverdue(b) && <Badge variant="destructive">overdue</Badge>}
        </div>
      ),
    },
    {
      key: "amount",
      header: "Amount",
      sortAccessor: (b) => (b.totalAmount ? Number(b.totalAmount) : null),
      cell: (b) => (b.totalAmount ? formatCurrency(Number(b.totalAmount)) : "—"),
    },
    {
      key: "invoice",
      header: "Invoice #",
      sortAccessor: (b) => b.fakturoidInvoiceNumber,
      cellClassName: "font-mono text-xs text-muted-foreground",
      cell: (b) => b.fakturoidInvoiceNumber ?? "—",
    },
    {
      key: "items",
      header: "Items",
      sortAccessor: (b) => b._count.items,
      cell: (b) => b._count.items,
    },
    {
      key: "createdAt",
      header: "Created",
      sortAccessor: (b) => b.createdAt,
      cellClassName: "text-sm text-muted-foreground",
      cell: (b) => <LocalDate value={b.createdAt} />,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Billing</h1>
          <p className="text-muted-foreground text-sm mt-1">
            The invoice-batch ledger. Open a report to review its lines and issue the invoice; its
            payment comes from the accounting.
          </p>
        </div>
        <Button asChild>
          <Link to="/billing/new">Generate report</Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Paid</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(stats.totalPaid)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats.totalPending)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Batches
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{total}</div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <SearchInput placeholder="Search by customer or invoice number…" />
          <Select
            aria-label="Filter by status"
            value={search.status ?? ""}
            onChange={(v) => setFilter("status", v || undefined)}
            options={[
              { value: "", label: "All statuses" },
              ...BATCH_STATUSES.map((s) => ({ value: s, label: s })),
            ]}
          />
          <Select
            aria-label="Filter by customer"
            value={search.customerId ?? ""}
            onChange={(v) => setFilter("customerId", v || undefined)}
            options={[
              { value: "", label: "All customers" },
              ...customers.map((cust) => ({ value: cust.id, label: cust.name })),
            ]}
          />
        </div>

        <DataTable
          columns={columns}
          rows={batches}
          rowKey={(b) => b.id}
          sort={sort}
          rowLink={(b) => `/billing/${b.id}`}
          rowClassName={() => "hover:bg-accent/40"}
          emptyState="No invoice batches found."
        />

        <Pagination
          total={total}
          page={page}
          pageSize={pageSize}
          pageSizes={PAGE_SIZE_OPTIONS}
          storageKey="billing"
          basePath="/billing"
          searchParams={{
            q: search.q,
            status: search.status,
            customerId: search.customerId,
            sort: search.sort,
            dir: search.dir,
            pageSize: search.pageSize,
          }}
        />
      </div>
    </div>
  );
}
